import { useState, useMemo } from "react";
import PortalLayout from "@/components/PortalLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  AlertTriangle, Wrench, Shield, Users, Ship,
  Download, ChevronDown, ChevronUp, Search,
  BookOpen, Target, Clock, CheckCircle2, FileText, Filter
} from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { LessonCommentThread } from "@/components/LessonCommentThread";
import { usePortalCompany } from "@/hooks/usePortalCompany";
import { CLIENT_DISPLAY_NAMES } from "@/data/portalMockData";

type Priority = "CRITICAL" | "HIGH" | "LOW";
type Category = "spare-parts" | "maintenance" | "planned-maintenance" | "operational" | "crew" | "design";
type KpiFilter = "all" | "critical" | "high" | "moderate" | "in-progress" | "closed";

const CATEGORIES: { key: Category; label: string; icon: typeof Wrench; accent: string }[] = [
  { key: "spare-parts", label: "Spare Parts & Logistics", icon: Wrench, accent: "#E67E22" },
  { key: "maintenance", label: "Maintenance Organization", icon: Users, accent: "#2980B9" },
  { key: "planned-maintenance", label: "Planned Maintenance Periods", icon: Clock, accent: "#27AE60" },
  { key: "operational", label: "Operational Readiness", icon: Target, accent: "#8E44AD" },
  { key: "crew", label: "Crew & Training", icon: Shield, accent: "#16A085" },
  { key: "design", label: "Design Lessons", icon: Ship, accent: "#C0392B" },
];

const priorityBadge: Record<Priority, { label: string; cls: string }> = {
  CRITICAL: { label: "Critical", cls: "bg-red-100 text-red-800 border-red-200" },
  HIGH: { label: "High", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  LOW: { label: "Moderate", cls: "bg-slate-100 text-slate-600 border-slate-200" },
};

const statusBadge: Record<string, { label: string; dot: string }> = {
  open: { label: "Open", dot: "bg-slate-400" },
  "in-progress": { label: "In Progress", dot: "bg-blue-500" },
  closed: { label: "Closed", dot: "bg-emerald-500" },
};

const LessonsLearned = () => {
  const [search, setSearch] = useState("");
  const [priorityFilter, setPriorityFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [kpiFilter, setKpiFilter] = useState<KpiFilter>("all");
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [showFilters, setShowFilters] = useState(false);
  const { companyName } = usePortalCompany();

  const clientKey = useMemo(() => {
    if (!companyName) return null;
    const entry = Object.entries(CLIENT_DISPLAY_NAMES).find(([, display]) =>
      companyName.toLowerCase().includes(display.toLowerCase().split(" ")[0])
    );
    return entry ? entry[0] : null;
  }, [companyName]);

  const { data: lessons = [], isLoading } = useQuery({
    queryKey: ["lessons-learned"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("lessons_learned" as any)
        .select("*")
        .order("id");
      if (error) throw error;
      return data as any[];
    },
  });

  const toggleExpand = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const filtered = useMemo(() => {
    return lessons.filter((l: any) => {
      if (kpiFilter === "critical" && l.priority !== "CRITICAL") return false;
      if (kpiFilter === "high" && l.priority !== "HIGH") return false;
      if (kpiFilter === "moderate" && l.priority !== "LOW") return false;
      if (kpiFilter === "in-progress" && l.status !== "in-progress") return false;
      if (kpiFilter === "closed" && l.status !== "closed") return false;
      if (priorityFilter !== "all" && l.priority !== priorityFilter) return false;
      if (statusFilter !== "all" && l.status !== statusFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        return l.id.toLowerCase().includes(q) || l.title.toLowerCase().includes(q) || l.root_cause?.toLowerCase().includes(q) || l.corrective_action?.toLowerCase().includes(q);
      }
      return true;
    });
  }, [lessons, kpiFilter, priorityFilter, statusFilter, search]);

  const totalCritical = lessons.filter((l: any) => l.priority === "CRITICAL").length;
  const totalHigh = lessons.filter((l: any) => l.priority === "HIGH").length;
  const totalModerate = lessons.filter((l: any) => l.priority === "LOW").length;
  const closedCount = lessons.filter((l: any) => l.status === "closed").length;
  const inProgressCount = lessons.filter((l: any) => l.status === "in-progress").length;
  const openCount = lessons.filter((l: any) => l.status === "open").length;
  const completionRate = lessons.length ? Math.round((closedCount / lessons.length) * 100) : 0;

  const activeFilterLabel = kpiFilter !== "all"
    ? `Showing: ${kpiFilter === "critical" ? "Critical" : kpiFilter === "high" ? "High Priority" : kpiFilter === "moderate" ? "Moderate" : kpiFilter === "in-progress" ? "In Progress" : "Closed"} (${filtered.length})`
    : null;

  if (isLoading) {
    return (
      <PortalLayout>
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-2 border-[hsl(211,100%,40%)]/30 border-t-[hsl(211,100%,40%)] rounded-full animate-spin" />
        </div>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout>
      <div className="max-w-5xl mx-auto space-y-8">

        {/* ── Page Header ── */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <p className="text-[10px] font-bold tracking-[3px] uppercase text-[hsl(211,100%,40%)]/50 mb-2">
              LL-AIRCAT35-001 Rev. 0 · Confidential
            </p>
            <h1 className="text-3xl font-bold text-[hsl(215,25%,17%)] leading-tight">
              Lessons Learned
            </h1>
            <p className="text-sm text-[hsl(215,12%,50%)] mt-1">
              AIRCAT 35 Crewliner Operations — Angola insights applied to Guyana
            </p>
          </div>
          <a
            href="/documents/EMGL003-Final_Lessons_Learned_AIRCAT35_Guyana.docx"
            download
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[hsl(211,100%,40%)] text-white text-sm font-semibold hover:bg-[hsl(211,100%,45%)] transition-colors shadow-sm"
          >
            <Download className="w-4 h-4" /> Download Full Report
          </a>
        </div>

        {/* ── Executive Summary ── */}
        <div className="rounded-xl border border-[hsl(211,100%,40%)]/15 bg-gradient-to-r from-[hsl(211,100%,40%)]/5 to-transparent p-6">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-[hsl(211,100%,40%)]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
              <FileText className="w-4 h-4 text-[hsl(211,100%,40%)]" />
            </div>
            <div>
              <h2 className="font-semibold text-[hsl(215,25%,17%)] mb-2">Executive Summary</h2>
              <p className="text-sm text-[hsl(215,12%,50%)] leading-relaxed">
                This document captures operational lessons learned from the AIRCAT 35 Crewliner fleet deployment in Angola (TotalEnergies) and applies them to planned crew transfer operations for ExxonMobil Worldwide's Stabroek Block FPSOs. The three AIRCAT 35 Crewliners have been performing daily 250+ nm round-trip crew transfers since late 2024, providing a first-of-type operational dataset for SES technology.
              </p>
            </div>
          </div>
        </div>

        {/* ── KPI Strip ── */}
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
          {[
            { key: "all" as KpiFilter, value: lessons.length, label: "Total Lessons", color: "hsl(211,100%,40%)" },
            { key: "critical" as KpiFilter, value: totalCritical, label: "Critical", color: "#DC2626" },
            { key: "high" as KpiFilter, value: totalHigh, label: "High Priority", color: "#D97706" },
            { key: "moderate" as KpiFilter, value: totalModerate, label: "Moderate", color: "#7C3AED" },
            { key: "in-progress" as KpiFilter, value: inProgressCount, label: "In Progress", color: "#2563EB" },
            { key: "closed" as KpiFilter, value: `${completionRate}%`, label: "Closed", color: "#059669" },
          ].map((kpi) => {
            const active = kpiFilter === kpi.key;
            return (
              <button
                key={kpi.key}
                onClick={() => setKpiFilter(active ? "all" : kpi.key)}
                className={`rounded-xl border-2 py-4 text-center transition-all duration-200 ${
                  active
                    ? "shadow-lg scale-[1.03] bg-white"
                    : "border-[hsl(220,15%,92%)] bg-white hover:border-[hsl(220,15%,85%)] hover:shadow-sm"
                }`}
                style={active ? { borderColor: kpi.color } : undefined}
              >
                <p className="text-2xl font-bold" style={{ color: kpi.color }}>{kpi.value}</p>
                <p className="text-[9px] font-semibold tracking-[1.5px] uppercase text-[hsl(215,12%,50%)] mt-1">{kpi.label}</p>
              </button>
            );
          })}
        </div>

        {/* ── Progress Bar ── */}
        <div className="rounded-xl border border-[hsl(220,15%,90%)] bg-white p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-[hsl(215,25%,17%)]">Implementation Progress</h3>
            <span className="text-xs text-[hsl(215,12%,50%)]">{closedCount} of {lessons.length} corrective actions closed</span>
          </div>
          <div className="w-full h-2.5 rounded-full bg-[hsl(220,15%,93%)] overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{
                width: `${completionRate}%`,
                background: "linear-gradient(90deg, #059669, #10B981)",
              }}
            />
          </div>
          <div className="flex items-center gap-6 mt-3">
            <div className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full bg-slate-400" />
              <span className="text-xs text-[hsl(215,12%,50%)]">Open {openCount}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full bg-blue-500" />
              <span className="text-xs text-[hsl(215,12%,50%)]">In Progress {inProgressCount}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
              <span className="text-xs text-[hsl(215,12%,50%)]">Closed {closedCount}</span>
            </div>
          </div>
        </div>

        {/* ── Search & Filter Toggle ── */}
        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(215,12%,50%)]" />
            <Input
              placeholder="Search by ID, title, root cause, or corrective action..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9 h-10"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`inline-flex items-center gap-1.5 px-3 h-10 rounded-lg border text-xs font-medium transition-colors ${
              showFilters || priorityFilter !== "all" || statusFilter !== "all"
                ? "bg-[hsl(211,100%,40%)]/10 border-[hsl(211,100%,40%)]/30 text-[hsl(211,100%,40%)]"
                : "border-[hsl(220,15%,90%)] text-[hsl(215,12%,50%)] hover:border-[hsl(220,15%,80%)]"
            }`}
          >
            <Filter className="w-3.5 h-3.5" /> Filters
          </button>
        </div>

        {showFilters && (
          <div className="flex gap-3 -mt-4">
            <Select value={priorityFilter} onValueChange={setPriorityFilter}>
              <SelectTrigger className="w-[160px]"><SelectValue placeholder="Priority" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Priorities</SelectItem>
                <SelectItem value="CRITICAL">Critical</SelectItem>
                <SelectItem value="HIGH">High</SelectItem>
                <SelectItem value="LOW">Moderate</SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[160px]"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="in-progress">In Progress</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
            {(priorityFilter !== "all" || statusFilter !== "all") && (
              <button onClick={() => { setPriorityFilter("all"); setStatusFilter("all"); }} className="text-xs text-[hsl(211,100%,40%)] underline hover:no-underline self-center">
                Clear filters
              </button>
            )}
          </div>
        )}

        {/* Active filter indicator */}
        {activeFilterLabel && (
          <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[hsl(211,100%,40%)]/5 border border-[hsl(211,100%,40%)]/15 -mt-4">
            <span className="text-xs text-[hsl(211,100%,40%)] font-medium">{activeFilterLabel}</span>
            <button onClick={() => setKpiFilter("all")} className="text-[10px] text-[hsl(211,100%,40%)] underline hover:no-underline ml-auto">Clear</button>
          </div>
        )}

        {/* ── Lessons by Category (Sectioned Layout) ── */}
        {CATEGORIES.map(cat => {
          const catLessons = filtered.filter((l: any) => l.category === cat.key);
          if (catLessons.length === 0) return null;

          return (
            <section key={cat.key}>
              {/* Category Header */}
              <div className="flex items-center gap-3 mb-4">
                <div
                  className="w-9 h-9 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: `${cat.accent}15` }}
                >
                  <cat.icon className="w-4.5 h-4.5" style={{ color: cat.accent }} />
                </div>
                <div>
                  <h2 className="text-base font-bold text-[hsl(215,25%,17%)]">{cat.label}</h2>
                  <p className="text-[10px] text-[hsl(215,12%,50%)]">{catLessons.length} item{catLessons.length !== 1 ? "s" : ""}</p>
                </div>
              </div>

              {/* Lesson Cards */}
              <div className="space-y-2 mb-8">
                {catLessons.map((lesson: any) => {
                  const expanded = expandedIds.has(lesson.id);
                  const pCfg = priorityBadge[lesson.priority as Priority] || priorityBadge.HIGH;
                  const sCfg = statusBadge[lesson.status] || statusBadge.open;
                  return (
                    <div
                      key={lesson.id}
                      className={`rounded-xl border bg-white transition-all duration-200 ${
                        expanded ? "border-[hsl(211,100%,40%)]/25 shadow-md" : "border-[hsl(220,15%,92%)] hover:border-[hsl(220,15%,85%)]"
                      }`}
                    >
                      {/* Row Header */}
                      <button
                        onClick={() => toggleExpand(lesson.id)}
                        className="w-full text-left px-5 py-3.5 flex items-center gap-4"
                      >
                        {/* ID Badge */}
                        <span
                          className="text-[11px] font-bold tracking-wide px-2 py-0.5 rounded border flex-shrink-0"
                          style={{ borderColor: cat.accent + "40", color: cat.accent, backgroundColor: cat.accent + "10" }}
                        >
                          {lesson.id}
                        </span>

                        {/* Title */}
                        <span className="flex-1 text-sm font-medium text-[hsl(215,25%,17%)] leading-snug line-clamp-2">
                          {lesson.title}
                        </span>

                        {/* Priority & Status */}
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${pCfg.cls}`}>
                            {pCfg.label}
                          </span>
                          <div className="flex items-center gap-1">
                            <div className={`w-2 h-2 rounded-full ${sCfg.dot}`} />
                            <span className="text-[10px] text-[hsl(215,12%,50%)]">{sCfg.label}</span>
                          </div>
                          {expanded
                            ? <ChevronUp className="w-4 h-4 text-[hsl(215,12%,50%)]" />
                            : <ChevronDown className="w-4 h-4 text-[hsl(215,12%,50%)]" />
                          }
                        </div>
                      </button>

                      {/* Expanded Detail */}
                      {expanded && (
                        <div className="px-5 pb-5 border-t border-[hsl(220,15%,93%)]">
                          <div className="grid md:grid-cols-2 gap-5 pt-4">
                            {/* Root Cause */}
                            <div className="rounded-lg border border-red-100 bg-red-50/50 p-4">
                              <p className="text-[10px] font-bold tracking-[1.5px] uppercase text-red-600/70 mb-2 flex items-center gap-1.5">
                                <AlertTriangle className="w-3 h-3" /> Root Cause — Angola
                              </p>
                              <p className="text-[13px] text-[hsl(215,25%,17%)]/75 leading-relaxed">{lesson.root_cause}</p>
                            </div>

                            {/* Corrective Action */}
                            <div className="rounded-lg border border-emerald-100 bg-emerald-50/50 p-4">
                              <p className="text-[10px] font-bold tracking-[1.5px] uppercase text-emerald-700/70 mb-2 flex items-center gap-1.5">
                                <CheckCircle2 className="w-3 h-3" /> Corrective Action — Guyana
                              </p>
                              <p className="text-[13px] text-[hsl(215,25%,17%)]/75 leading-relaxed">{lesson.corrective_action}</p>
                            </div>
                          </div>

                          {/* Closure Info */}
                          {lesson.status === "closed" && lesson.closed_by_name && (
                            <div className="mt-4 p-3 rounded-lg bg-emerald-50 border border-emerald-200 flex items-center gap-2">
                              <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                              <div>
                                <p className="text-xs font-medium text-emerald-700">
                                  Closed by {lesson.closed_by_name} on {new Date(lesson.closed_at).toLocaleDateString()}
                                </p>
                                {lesson.closure_notes && <p className="text-xs text-emerald-600/70 mt-0.5">{lesson.closure_notes}</p>}
                              </div>
                            </div>
                          )}

                          {/* Q&A Thread */}
                          <div className="mt-4 pt-4 border-t border-[hsl(220,15%,93%)]">
                            <LessonCommentThread lessonId={lesson.id} variant="portal" />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          );
        })}

        {filtered.length === 0 && (
          <div className="text-center py-16 text-[hsl(215,12%,50%)]">
            <BookOpen className="w-8 h-8 mx-auto mb-3 opacity-30" />
            <p className="text-sm font-medium">No lessons match your current filters.</p>
            <button onClick={() => { setKpiFilter("all"); setPriorityFilter("all"); setStatusFilter("all"); setSearch(""); }} className="text-xs text-[hsl(211,100%,40%)] underline mt-2">
              Reset all filters
            </button>
          </div>
        )}

        {/* ── Reference Tables ── */}
        <div className="space-y-6 pt-4 border-t border-[hsl(220,15%,90%)]">
          <h2 className="text-lg font-bold text-[hsl(215,25%,17%)]">Reference Information</h2>

          {/* Operational Context */}
          <div className="rounded-xl border border-[hsl(220,15%,90%)] bg-white overflow-hidden">
            <div className="px-5 py-3 border-b border-[hsl(220,15%,93%)] bg-[hsl(220,20%,97%)]">
              <h3 className="text-sm font-semibold text-[hsl(215,25%,17%)]">Operational Context — Angola vs. Guyana</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[hsl(220,15%,93%)]">
                    <th className="text-left py-2.5 px-5 text-[10px] font-bold tracking-wider uppercase text-[hsl(215,12%,50%)]">Parameter</th>
                    <th className="text-left py-2.5 px-5 text-[10px] font-bold tracking-wider uppercase text-[hsl(215,12%,50%)]">Angola</th>
                    <th className="text-left py-2.5 px-5 text-[10px] font-bold tracking-wider uppercase text-[hsl(215,12%,50%)]">Guyana (Planned)</th>
                  </tr>
                </thead>
                <tbody className="text-[hsl(215,25%,17%)]/70">
                  {[
                    ["Operator", "TotalEnergies' local provider", "Velocity Offshore for ExxonMobil Worldwide"],
                    ["Vessel Fleet", "3x AIRCAT 35 Crewliner", "AIRCAT 35 Crewliner (number per requirements)"],
                    ["Base Port", "Luanda, Angola", "Georgetown, Guyana"],
                    ["Offshore Distance", "~125–150 nm one-way", "~100–150 nm one-way (Stabroek Block)"],
                    ["Round Trip", "150–300 nm daily", "200–300 nm daily"],
                    ["Typical Speed", "35–40+ knots", "35–40+ knots"],
                    ["Sea Conditions", "South Atlantic swell, moderate tropical", "Atlantic swell, tropical, trade wind seas"],
                    ["Classification", "ABS (HSC)", "ABS (HSC)"],
                    ["Daily Pax", "Up to 80 per vessel per run", "Up to 80 per vessel per run"],
                    ["OEM Proximity", "Remote from service hubs", "Closer to US-based hubs (Houston, Miami)"],
                  ].map(([param, angola, guyana], i) => (
                    <tr key={i} className="border-b border-[hsl(220,15%,95%)] last:border-0">
                      <td className="py-2.5 px-5 font-medium text-[hsl(215,25%,17%)]">{param}</td>
                      <td className="py-2.5 px-5">{angola}</td>
                      <td className="py-2.5 px-5">{guyana}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Maintenance Calendar */}
          <div className="rounded-xl border border-[hsl(220,15%,90%)] bg-white overflow-hidden">
            <div className="px-5 py-3 border-b border-[hsl(220,15%,93%)] bg-[hsl(220,20%,97%)]">
              <h3 className="text-sm font-semibold text-[hsl(215,25%,17%)]">Proposed Maintenance Calendar — Guyana</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[hsl(220,15%,93%)]">
                    <th className="text-left py-2.5 px-5 text-[10px] font-bold tracking-wider uppercase text-[hsl(215,12%,50%)]">Period</th>
                    <th className="text-left py-2.5 px-5 text-[10px] font-bold tracking-wider uppercase text-[hsl(215,12%,50%)]">Duration</th>
                    <th className="text-left py-2.5 px-5 text-[10px] font-bold tracking-wider uppercase text-[hsl(215,12%,50%)]">Scope</th>
                  </tr>
                </thead>
                <tbody className="text-[hsl(215,25%,17%)]/70">
                  {[
                    ["Nightly Turnaround", "~10–12 hours", "PMS tasks, defect repairs, consumable replenishment, air cushion inspection, waterjet cleaning, pre-departure checks"],
                    ["Weekly Maintenance", "24 hours (1 day/week)", "MTU W1 service, waterjet intermediate inspection, extended system checks, stores replenishment"],
                    ["Annual Out-of-Water", "7–14 days", "ABS HSC annual survey, hull/antifouling, waterjet overhaul, air cushion major service, MTU W4"],
                    ["5-Year Special Survey", "14–21 days", "ABS special survey, complete dry-dock, MTU W6 overhaul, waterjet major overhaul, full air cushion renewal"],
                  ].map(([period, duration, scope], i) => (
                    <tr key={i} className="border-b border-[hsl(220,15%,95%)] last:border-0">
                      <td className="py-2.5 px-5 font-medium text-[hsl(215,25%,17%)] whitespace-nowrap">{period}</td>
                      <td className="py-2.5 px-5 whitespace-nowrap">{duration}</td>
                      <td className="py-2.5 px-5">{scope}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Night Support Team */}
          <div className="rounded-xl border border-[hsl(220,15%,90%)] bg-white overflow-hidden">
            <div className="px-5 py-3 border-b border-[hsl(220,15%,93%)] bg-[hsl(220,20%,97%)]">
              <h3 className="text-sm font-semibold text-[hsl(215,25%,17%)]">Night Support Team — Recommended Composition</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[hsl(220,15%,93%)]">
                    <th className="text-left py-2.5 px-5 text-[10px] font-bold tracking-wider uppercase text-[hsl(215,12%,50%)]">Role</th>
                    <th className="text-left py-2.5 px-5 text-[10px] font-bold tracking-wider uppercase text-[hsl(215,12%,50%)]">Qty</th>
                    <th className="text-left py-2.5 px-5 text-[10px] font-bold tracking-wider uppercase text-[hsl(215,12%,50%)]">Qualifications</th>
                    <th className="text-left py-2.5 px-5 text-[10px] font-bold tracking-wider uppercase text-[hsl(215,12%,50%)]">Responsibilities</th>
                  </tr>
                </thead>
                <tbody className="text-[hsl(215,25%,17%)]/70">
                  {[
                    ["Shore Maintenance Team Lead", "1", "Senior marine engineer; MTU or Kongsberg OEM training", "Prioritize tasks, sign off readiness, OEM liaison"],
                    ["Mechanical Technician — Engines", "1", "MTU ValueCare certified", "Engine PMS, W1/W2 services, oil sampling"],
                    ["Mechanical/Hydraulic Technician — Waterjets", "1", "Kongsberg Kamewa waterjet trained", "Waterjet inspection, anodes, steering system"],
                    ["Electro-Technical Officer (ETO)", "1", "Marine electro-technical officer", "Navigation, GMDSS, power management, SES controls"],
                    ["Oiler", "2", "Assist mechanical technicians", "General support to engineering team"],
                    ["Certified Welder", "1 (on-call)", "Aluminium hull repair; NDT capable", "Hull repairs, scheduled or on-call"],
                  ].map(([role, qty, quals, resp], i) => (
                    <tr key={i} className="border-b border-[hsl(220,15%,95%)] last:border-0">
                      <td className="py-2.5 px-5 font-medium text-[hsl(215,25%,17%)]">{role}</td>
                      <td className="py-2.5 px-5">{qty}</td>
                      <td className="py-2.5 px-5">{quals}</td>
                      <td className="py-2.5 px-5">{resp}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </PortalLayout>
  );
};

export default LessonsLearned;
